// The pure rules that decide WHICH price the site shows: the scope specificity
// ladder, the coupon pick, the live-window predicates, and the two sort
// comparators.
//
// Extracted from load.ts so the family editor's pricing matrix resolves prices
// through the SAME code the price cache does. The editor's "Product page shows"
// row is only worth having if it cannot drift from the render path — a second
// implementation of "which offer wins" would drift the first time either side
// changed, and the row would quietly start lying.
//
// Everything here is pure and clock-free: `now` is always passed in. The cache
// loader runs during a prerender, where reading the wall clock is forbidden, and
// the editor's builder is unit-tested against a fixed clock.

// Sentinel for a null seller.position, so a seller with no position sorts last
// (lowest priority). position is a smallint in postgres; 1e9 is far out of range.
export const POSITION_SENTINEL = 1_000_000_000

// Scope specificity — LOWER wins. A seller's product-scoped offer supersedes its
// tier-scoped one, which supersedes its release-wide one, leaving at most one
// effective offer per (product, seller). Callers name the scope with whatever
// they hold: ids in the loader (product_id / level_id), refs in the editor's
// draft (modelRef / levelRef).
export const offerSpecificity = (scope: { product: number | string | null; tier: number | string | null }): number => {
  if (scope.product != null) {
    return 0
  }
  return scope.tier != null ? 1 : 2
}

// The only coupon fields the pick needs. Deliberately narrow, so the loader's DB
// row and the editor's catalog entry both satisfy it and each builds its own
// richer object from the winner.
export interface CouponDiscount {
  amountOff: number | null
  percentOff: number | null
}

export interface BestCoupon<T> {
  coupon: T
  discount: number
  discountPrice: number
}

// Pick an offer's coupon: the largest absolute discount, matching the
// `LATERAL … LIMIT 1` in product_prices_v1. Several active coupons on one offer
// is rare but legal, and only the most-discounted one is ever applied. A coupon
// carrying neither an amount nor a percentage discounts nothing and is skipped.
export const pickBestCoupon = <T extends CouponDiscount>(
  coupons: readonly T[],
  price: number
): BestCoupon<T> | null => {
  let best: BestCoupon<T> | null = null
  for (const coupon of coupons) {
    const amount = coupon.amountOff ?? 0
    const percent = coupon.percentOff ?? 0
    if (!amount && !percent) {
      continue
    }
    const discount = amount > 0 ? amount : (percent * price) / 100
    if (!best || discount > best.discount) {
      best = { coupon, discount, discountPrice: price - discount }
    }
  }
  return best
}

// Why the site is ignoring an offer — null when it isn't.
export type OfferNotLiveReason = 'inactive' | 'scheduled' | 'ended'

// The JS twin of the WHERE clause in loadPriceCache's offers query (and of the
// price views' own filters) — change both together.
//
// The editor deliberately LOADS inactive and scheduled offers, because those are
// exactly the rows a human needs to find and fix. Which is precisely why it needs
// this: to show WHICH of them the site is currently ignoring, and why. Returning
// the reason (rather than a bare boolean plus a second, drifting copy of these
// branches in a badge component) keeps one implementation of the rule.
//
// The seller's own `active` flag is a separate check the caller applies — the
// loader joins it in SQL, the editor holds it on the catalog row.
export const offerNotLiveReason = (
  offer: { active: boolean; startDate: string | null; endDate: string | null },
  now: Date
): OfferNotLiveReason | null => {
  if (!offer.active) {
    return 'inactive'
  }
  if (offer.startDate != null && new Date(offer.startDate) > now) {
    return 'scheduled'
  }
  if (offer.endDate != null && new Date(offer.endDate) <= now) {
    return 'ended'
  }
  return null
}

// Would the site use this offer right now?
export const offerIsLive = (
  offer: { active: boolean; startDate: string | null; endDate: string | null },
  now: Date
): boolean => offerNotLiveReason(offer, now) === null

// Resolve two LIVE price periods from the same seller at the same exact scope.
// Every row is a peer: dates only decide whether it is available, never whether
// it outranks another live row. The lowest checkout price after the offer's best
// live coupon wins; raw price and the stable offer key make exact ties explicit.
//
// Callers filter by offerIsLive and calculate coupons first. Keeping both the
// clock and coupon lookup out of this comparator makes it usable by the price
// cache, SQL's matching row order, and the draft editor.
export interface OfferPricePriority {
  discountPrice: number
  price: number
  offerKey: number | string
}

export const cmpOfferPrice = (a: OfferPricePriority, b: OfferPricePriority): number => {
  if (a.discountPrice !== b.discountPrice) {
    return a.discountPrice - b.discountPrice
  }
  if (a.price !== b.price) {
    return a.price - b.price
  }
  if (typeof a.offerKey === 'number' && typeof b.offerKey === 'number') {
    return a.offerKey - b.offerKey
  }
  const aKey = String(a.offerKey)
  const bKey = String(b.offerKey)
  return aKey < bKey ? -1 : aKey > bKey ? 1 : 0
}

// The coupon twin of offerIsLive: the WHERE clause in loadPriceCache's coupons
// query — active, unexpired, and actually carrying a discount.
export const couponIsLive = (
  coupon: CouponDiscount & { active: boolean; expiresAt: string | null },
  now: Date
): boolean => {
  if (!coupon.active) {
    return false
  }
  if (coupon.expiresAt != null && new Date(coupon.expiresAt) <= now) {
    return false
  }
  return coupon.amountOff != null || coupon.percentOff != null
}

// The two orderings the price views expose. `sellerPosition` is already coalesced
// through POSITION_SENTINEL by the caller.
export interface SortableOffer {
  sellerPosition: number
  discountPrice: number
}

// sort_best: the seller's priority decides and price only breaks a tie. This is
// what picks the offer a product page leads with — which is what makes a seller's
// `position` commercially load-bearing, and why `sellers` needed a revalidate
// trigger before any of this could be edited.
export const cmpBest = (a: SortableOffer, b: SortableOffer): number => {
  if (a.sellerPosition !== b.sellerPosition) {
    return a.sellerPosition - b.sellerPosition
  }
  return a.discountPrice - b.discountPrice
}

// sort_lowest: price decides and the higher-priority seller breaks a tie.
export const cmpLowest = (a: SortableOffer, b: SortableOffer): number => {
  if (a.discountPrice !== b.discountPrice) {
    return a.discountPrice - b.discountPrice
  }
  return a.sellerPosition - b.sellerPosition
}
